import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  CreateQuizInput,
  DeleteQuizResponse,
  QuizContentTypeOption,
  QuizDetail,
  QuizSettings,
  QuizTypeOption,
  QuizzesListParams,
  QuizzesListResponse,
  ResetAttemptsInput,
  ResetAttemptsResponse,
  UpdateQuizInput,
  UpdateQuizSettingsInput,
  UpdateQuizStatusInput,
  UpdateQuizStatusResponse,
  VideoUploadUrlInput,
  VideoUploadUrlResponse,
} from 'models/Quiz';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

const BASE = '/quiz';
const BASE_TYPES = '/quiz-types';
const BASE_SETTINGS = '/quiz-settings';

export const QuizService = {
  /**
   * Paso 1 del flujo touch_storage: obtiene una presigned PUT URL para subir
   * el video directamente a R2. El PUT (Paso 2) va directo al bucket y NO
   * debe pasar por MainBackendAxios — ver README de portal-go para el flujo
   * completo de 3 pasos.
   */
  getVideoUploadUrl: async (
    body: VideoUploadUrlInput
  ): Promise<ApiResponse<VideoUploadUrlResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/video-upload-url`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getContentTypes: async (): Promise<
    ApiResponse<QuizContentTypeOption[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/content-types`);
      return {
        success: true,
        status: response.status,
        // BFF devuelve { data: [...] } — extraemos el array interno
        data: response?.data?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  createQuiz: async (
    body: CreateQuizInput
  ): Promise<ApiResponse<QuizDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(`${BASE}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getQuizzes: async (
    params: QuizzesListParams
  ): Promise<ApiResponse<QuizzesListResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}`, { params });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getQuizById: async (
    id: string
  ): Promise<ApiResponse<QuizDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateQuiz: async (
    id: string,
    body: UpdateQuizInput
  ): Promise<ApiResponse<QuizDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/${id}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  deleteQuiz: async (
    id: string
  ): Promise<ApiResponse<DeleteQuizResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.delete(`${BASE}/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateQuizStatus: async (
    id: string,
    body: UpdateQuizStatusInput
  ): Promise<ApiResponse<UpdateQuizStatusResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/status`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  resetAttempts: async (
    id: string,
    body: ResetAttemptsInput
  ): Promise<ApiResponse<ResetAttemptsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/${id}/reset-attempts`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getQuizTypes: async (): Promise<
    ApiResponse<QuizTypeOption[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE_TYPES}`);
      return {
        success: true,
        status: response.status,
        // BFF devuelve { data: [...] } — extraemos el array interno
        data: response?.data?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getQuizSettings: async (): Promise<
    ApiResponse<QuizSettings | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE_SETTINGS}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateQuizSettings: async (
    body: UpdateQuizSettingsInput
  ): Promise<ApiResponse<QuizSettings | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE_SETTINGS}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
